import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MenuItem from 'particles/MenuItem'
import Text from 'particles/Text'
import RectangleButton from 'particles/Button/RectangleButton'
import Menu from 'particles/Menu'
import { useDispatch, useSelector } from 'react-redux'
import Chip from 'particles/Chip'
import { selectTeamConstants } from 'state/constants'
import { getTeamTypeOptions } from './Team/Teams/utils'
import { setTeamTypeFilters } from 'state/filter'
import styled from 'particles/themes/styled'
import Tooltip from 'particles/Tooltip'

interface TeamTypeFilterProps {
    page: 'campaign_list' | 'submission_list'
}

export const TeamTypeFilter: React.FC<TeamTypeFilterProps> = ({ page }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleClickTeamTypeFilter = () => {
        if (page === 'campaign_list') {
            dispatch(
                setTeamTypeFilters({
                    campaign_list: {
                        isOpen: true,
                        selected: [],
                    },
                }),
            )
        } else {
            dispatch(
                setTeamTypeFilters({
                    submission_list: {
                        isOpen: true,
                        selected: [],
                    },
                }),
            )
        }

        handleClose()
    }

    const menuItems = [
        <MenuItem.Text
            onClick={handleClickTeamTypeFilter}
            text={t('modalWindow.enterTeamTypeHeader')}
            key={'teamTypeFilter'}
        />,
    ]

    const handleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return menuItems.length > 0 ? (
        <>
            <RectangleButton
                size="s"
                icon="funnel"
                label={t('button.filter')}
                form="text"
                color="neutral"
                onClick={handleFilter}
            />
            <Menu size="s" width={226} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
                <MenuItem.Title text={t('dropdownMenu.filterByHeader')} case="uppercase" />
                {menuItems}
            </Menu>
        </>
    ) : (
        <></>
    )
}

interface ChipFilterProps<T> {
    id?: string
    isOpen?: boolean
    selected: string[]
    onSelectFilter?: (option: string[]) => void
    onDeleteChip?: () => void
}

export const TeamTypeChipFilter = <T,>({ id, onSelectFilter, onDeleteChip, isOpen, selected }: ChipFilterProps<T>) => {
    const [t] = useTranslation()
    const teamConstants = useSelector(selectTeamConstants)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const teamOptions = getTeamTypeOptions(t)
    const chipRef = useRef(null)

    const teamFilterItems = useMemo(() => {
        const teamTypes = teamConstants?.types || []

        return teamTypes.map(type => {
            return {
                label: teamOptions[type.id],
                value: type.id,
            }
        })
    }, [teamConstants])

    useEffect(() => {
        if (chipRef.current && selected.length === 0) {
            setAnchorEl(chipRef.current)
        }
    }, [chipRef.current, selected])

    useEffect(() => {
        onDeleteChip?.()
    }, [])

    const handleOnClickChip = (e: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(e.currentTarget)
    }

    const handleOnCloseChip = () => {
        setAnchorEl(null)
    }

    const handleOnChange = (option: string) => {
        const newSelected = selected.includes(option) ? selected.filter(item => item !== option) : [...selected, option]
        onSelectFilter?.(newSelected)
    }

    const handleDeleteChip = () => {
        onDeleteChip?.()
    }
    const getChipLabel = () => {
        let base = t('modalWindow.enterTeamTypeHeader')

        if (selected.length) {
            const selectedLabel = selected.map(item => teamOptions[item]?.title)
            base += `: ${selectedLabel.join(',')}`
        }

        return base
    }

    if (!isOpen && !selected.length) {
        return null
    }

    return (
        <>
            <Chip
                ref={chipRef}
                id={id}
                label={getChipLabel()}
                onDelete={handleDeleteChip}
                onClick={handleOnClickChip}
            />
            <Menu size="s" open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleOnCloseChip}>
                <MenuItem.Title text={t('dropdownMenu.filterByValueHeader')} case="uppercase" />

                {teamFilterItems?.map((item, index: number) => (
                    <MenuItem.Checkbox
                        key={`filter-sort-${index}`}
                        tabIndex={index}
                        onClick={() => {
                            handleOnChange(item.value)
                        }}
                        text={item.label.title}
                        selected={selected.findIndex(option => option === item.value) !== -1}
                    />
                ))}
            </Menu>
        </>
    )
}

interface TeamTypeTagProps {
    label: string
}

export const TeamTypeTag = ({ label }: TeamTypeTagProps) => {
    const [tooltip, setTooltip] = useState<string | null>(null)

    const onTagHover = (isOverflow: boolean) => {
        if (isOverflow) {
            setTooltip(label)
        } else {
            setTooltip(null)
        }
    }
    return (
        <Tooltip label={tooltip} placement="bottom">
            <TeamTypeTagContainer>
                <Text
                    scale="caption500"
                    className="tag-text"
                    onMouseEnter={e => onTagHover(e.currentTarget.scrollWidth > e.currentTarget.offsetWidth)}
                >
                    {label}
                </Text>
            </TeamTypeTagContainer>
        </Tooltip>
    )
}

export const TeamTypeTagContainer = styled.div`
    display: block;
    width: fit-content;
    max-width: 100%;
    overflow: hidden;
    border-radius: 30px;
    padding: 2px 6px 4px 6px;
    background: ${({ theme }) => theme.colors.surface.tertiary};
    box-sizing: border-box;
    max-height: 22px;

    .tag-text {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
`
